import { inject, Injectable } from '@angular/core';
import { ConflictTopic, type ScheduleConflictModel } from '@app/reducers/orm/schedule-conflict/schedule-conflict.model';
import { featureFlag, FeatureService } from '@sb/shared/feature';
import { SbDialogService } from '@sb/ui/dialog';
import { parseDate } from '@shiftbase-com/ts-shared/utilities';
import { endOfDay, isPast } from 'date-fns';
import { filter, Observable, switchMap, take } from 'rxjs';

import { FindCoverPromptComponent, FindCoverPromptData } from './prompt/find-cover-prompt.component';
import { FindCoverAssignments, FindCoverDialogData, ScheduleFindCoverComponent } from './schedule-find-cover.component';
import { FindCoverSummaryComponent, FindCoverSummaryData } from './summary/find-cover-summary.component';

export interface CanFindCoverOptions {
  conflicts: ScheduleConflictModel[] | undefined;
  canEdit: boolean;
  date: string | undefined;
}

@Injectable({ providedIn: 'root' })
export class FindCoverService {
  #dialogService = inject(SbDialogService);
  #featureService = inject(FeatureService);

  /**
   * Absence is the only conflict wired up so far; the dialog itself is keyed on a
   * shift occurrence, so widening to the other topics happens here.
   */
  canFindCover({ conflicts, canEdit, date }: CanFindCoverOptions): boolean {
    // Read the flag per call: this is a root singleton, constructed before the
    // Unleash context is applied, so a cached value would pin it to false.
    if (!this.#featureService.isEnabled(featureFlag.TMP_AUTO_SCHEDULING)) {
      return false;
    }

    // The roster-edit permission is passed in rather than derived: the day view
    // synthesises a flat `canEdit`, the week/month pipeline a nested
    // `permissions.canEdit`, so only the host knows where its own value lives.
    if (!canEdit) {
      return false;
    }

    // A day that has passed cannot be staffed any more. End of day, not the date
    // itself: local midnight is already past by 00:00:01, which would take the
    // offer off today's shifts. An absent date leaves it standing — the grid
    // always supplies one, so it is not a past shift.
    if (date && isPast(endOfDay(parseDate(date)))) {
      return false;
    }

    return !!conflicts?.some((conflict) => conflict.topic === ConflictTopic.TIME_OFF);
  }

  /** The planner asked for cover on this shift, so the recommendation starts straight away. */
  open(occurrenceId: string): void {
    this.#openStepper([occurrenceId]).subscribe();
  }

  /**
   * Every shift the absent employee has today. The planner arrives here from saving an
   * absence rather than from asking for cover, so the prompt always comes first and the
   * recommendation is theirs to start — however much of the day is uncovered.
   *
   * The summary only earns its place when there was a run to account for: one shift is
   * already answered by the stepper the planner just left.
   */
  openForDay(occurrenceIds: string[]): void {
    if (occurrenceIds.length === 0) {
      return;
    }

    this.#openPrompt(occurrenceIds)
      .pipe(
        // The prompt decides what the run covers: every shift, or the single one the
        // planner picked off the list. Dismissing it yields nothing.
        filter((chosen): chosen is string[] => !!chosen?.length),
        switchMap((chosen) => this.#openStepper(chosen)),
        // Dismissing the stepper yields undefined: only a run that reached its end
        // is accounted for in a summary.
        filter((assigned): assigned is FindCoverAssignments => !!assigned),
        filter(() => occurrenceIds.length > 1),
      )
      // The summary always covers the whole day, even when the run walked one shift.
      .subscribe((assigned) => this.#openSummary(occurrenceIds, assigned));
  }

  // A stray click outside would drop a flow the planner is in the middle of, so the
  // backdrop is left inert. Closing deliberately — the close button, Escape — still works.
  #openPrompt(occurrenceIds: string[]): Observable<string[] | undefined> {
    return this.#dialogService
      .setSize('md')
      .open<FindCoverPromptComponent, FindCoverPromptData, string[]>(FindCoverPromptComponent, {
        data: { occurrenceIds },
        disableBackdropClose: true,
      })
      .closed.pipe(take(1));
  }

  #openStepper(occurrenceIds: string[]): Observable<FindCoverAssignments | undefined> {
    return this.#dialogService
      .setSize('md')
      .open<ScheduleFindCoverComponent, FindCoverDialogData, FindCoverAssignments>(ScheduleFindCoverComponent, {
        data: { occurrenceIds },
        disableBackdropClose: true,
      })
      .closed.pipe(take(1));
  }

  #openSummary(allOccurrenceIds: string[], assigned: FindCoverAssignments): void {
    this.#dialogService
      .setSize('md')
      .open<FindCoverSummaryComponent, FindCoverSummaryData, string>(FindCoverSummaryComponent, {
        data: { occurrenceIds: allOccurrenceIds, assignedEmployeeIds: assigned },
        disableBackdropClose: true,
      })
      .closed.pipe(
        take(1),
        filter((rerunOccurrenceId): rerunOccurrenceId is string => !!rerunOccurrenceId),
        switchMap((rerunOccurrenceId) => this.#openStepper([rerunOccurrenceId])),
      )
      // Covering a still-open shift is a detour from the summary, so it always comes
      // back to it — including when the planner backs out without assigning anyone.
      .subscribe((rerunAssigned) => this.#openSummary(allOccurrenceIds, { ...assigned, ...rerunAssigned }));
  }
}
